/**
 * Bulk UI strings for the full-app i18n sweep (merged into en / es dicts).
 */
import type { TranslationDict } from "@repo/core";

export const uiEn: TranslationDict = {
  "type.income": "Income",
  "type.expense": "Expense",
  "type.transfer": "Transfer",

  "person.household": "Household",
  "person.personal": "Personal",
  "person.partner": "Partner",
  "person.sideHustle": "Side Hustle",

  "method.snowball": "Snowball",
  "method.avalanche": "Avalanche",

  "tx.log": "Transactions Log",
  "tx.search": "Search…",
  "tx.allTypes": "All Types",
  "tx.allCategories": "All Categories",
  "tx.fromDate": "From date",
  "tx.toDate": "To date",
  "tx.minAmount": "Min",
  "tx.maxAmount": "Max",
  "tx.clearFilters": "Clear",
  "tx.entries": "{n} entries",
  "tx.noMatch": "No transactions match your filters.",
  "tx.loadMore": "Load more ({n} remaining)",
  "tx.importCsv": "Import CSV",
  "tx.csv": "CSV",
  "tx.add": "Add",
  "tx.edit": "Edit",
  "tx.delete": "Delete",
  "tx.editAria": "Edit transaction",
  "tx.deleteAria": "Delete transaction",
  "tx.deleteConfirm": "Delete this transaction?",
  "tx.col.date": "Date",
  "tx.col.type": "Type",
  "tx.col.amount": "Amount",
  "tx.col.category": "Category",
  "tx.col.account": "Account",
  "tx.col.person": "Person",
  "tx.col.notes": "Notes",

  "txModal.edit": "Edit Transaction",
  "txModal.new": "New Transaction",
  "txModal.editSub": "Update an existing entry",
  "txModal.newSub": "Record spending or income",
  "txModal.type": "Transaction Type",
  "txModal.amount": "Amount",
  "txModal.date": "Date",
  "txModal.person": "Person / Member",
  "txModal.personPh": "Household, John, Sarah…",
  "txModal.category": "Category",
  "txModal.chooseCategory": "— Choose Category —",
  "txModal.notes": "Notes / Description",
  "txModal.notesPh": "Optional details…",
  "txModal.recurring": "Recurring transaction",
  "txModal.freq.weekly": "Weekly",
  "txModal.freq.monthly": "Monthly",
  "txModal.freq.custom": "Custom interval",
  "txModal.intervalDays": "Days between",
  "txModal.recurringHelp": "Future entries are generated automatically. You can skip a single occurrence later.",
  "txModal.series": "Recurring series",
  "txModal.scopeOne": "This occurrence only",
  "txModal.scopeSeries": "Entire series (this and future)",
  "txModal.skip": "Skip this occurrence",
  "txModal.skipConfirm": "Skip this occurrence only? The rest of the series stays.",
  "txModal.saving": "Saving…",
  "txModal.update": "Update Transaction",
  "txModal.save": "Save Transaction",

  "csv.title": "Import CSV",
  "csv.intro": "Map columns from your bank export, preview rows, then import.",
  "csv.mapping": "Column mapping",
  "csv.mappingHint": "Left: column name from your CSV file. Right: assign each to a BudgetOS field.",
  "csv.preview": "Preview ({ok}/{total} valid)",
  "csv.ignore": "Ignore",
  "csv.ok": "OK",
  "csv.importing": "Importing…",
  "csv.importN": "Import {n} rows",
  "csv.readError": "Could not read CSV headers.",
  "csv.badDate": "Bad date",
  "csv.badAmount": "Bad amount",
  "csv.noCategory": "No category",

  "cal.subtitle": "Track daily expenses and no-spend streaks",
  "cal.prev": "Previous Month",
  "cal.next": "Next Month",
  "cal.streak": "No-Spend Streak",
  "cal.streakSub": "consecutive days",
  "cal.noSpendDays": "No-Spend Days",
  "cal.thisMonth": "this month",
  "cal.spendDays": "Spend Days",
  "cal.active": "active",
  "cal.totalSpent": "Total Spent",
  "cal.avgDay": "avg {amount}/day",
  "cal.title": "Spending Calendar (Click day for details)",
  "cal.legend.noSpend": "No Spend",
  "cal.legend.spend": "Spend Day",
  "cal.legend.today": "Today",
  "cal.badge.noSpend": "No-Spend Day",
  "cal.badge.spent": "Spent:",
  "cal.badge.income": "Income:",
  "cal.cleanTitle": "Clean Financial Streak!",
  "cal.cleanBody": "No spending recorded on this day. Keep it going!",
  "cal.txOnDate": "Transactions on this date ({n})",
  "cal.addForDate": "Add Transaction for this Date",

  "debt.title": "Debt Payoff Calculator",
  "debt.totalDebt": "Total Debt",
  "debt.totalInterest": "Total Interest",
  "debt.debtFreeDate": "Debt-Free Date",
  "debt.apr": "{n}% APR",
  "debt.currentBalance": "current balance",
  "debt.minPay": "Min Pay",
  "debt.extra": "Extra",
  "debt.interest": "Interest",
  "debt.months": "{n} months",
  "debt.viewSchedule": "View full amortization schedule",
  "debt.hideSchedule": "Hide amortization schedule",
  "debt.col.month": "Month",
  "debt.col.payment": "Payment",
  "debt.col.principal": "Principal",
  "debt.col.interest": "Interest",
  "debt.col.balance": "Balance",
  "debt.balance": "Balance ({symbol})",
  "debt.rate": "Rate (%)",
  "debt.minPayment": "Min Payment",
  "debt.extraPayment": "Extra Payment",
  "debt.chartTitle": "Combined Debt Balance Over Time",
  "debt.payoffOrderSnowball": "Payoff Order (Smallest Balance First)",
  "debt.payoffOrderAvalanche": "Payoff Order (Highest Rate First)",
  "debt.exportSchedule": "Export Schedule",
  "debt.edit": "Edit",

  "mb.exportMonth": "Export This Month",
  "mb.income": "Income",
  "mb.expenses": "Expenses",
  "mb.target": "target {amount}",
  "mb.budget": "budget {amount}",
  "mb.allocation": "Monthly Spending Allocation",
  "mb.byGroup": "By Group (Needs / Wants / Savings)",
  "mb.topExpense": "Top Expense Categories",
  "mb.incomeSources": "Income Sources",

  "rule.netIncome": "Monthly Net Income",
  "rule.distribution": "50/30/20 Distribution",
  "rule.breakdown": "Detailed Breakdown",
  "rule.visual": "Visual Comparison",
  "rule.targetPct": "Target: {n}% of income",
  "rule.actual": "Actual:",
  "rule.unclassified": "{n}% of income is unclassified or unspent",
  "rule.status.ahead": "Ahead of target",
  "rule.status.onTrack": "On track",
  "rule.status.below": "Below target",
  "rule.status.over": "Over budget",
  "rule.status.under": "Under target",

  "sf.title": "Sinking Funds",
  "sf.addFund": "Add Fund",
  "sf.totalSaved": "Total Saved",
  "sf.saved": "Saved",
  "sf.target": "Target",
  "sf.remaining": "Remaining",
  "sf.monthsLeft": "Months Left",
  "sf.needMonth": "Need/Month",
  "sf.contribute": "+ Contribute",
  "sf.contributePrompt": "Add to \"{name}\"\nCurrent: {current}\nAmount:",
  "sf.deleteConfirm": "Delete \"{name}\"?",
  "sf.due": "Due {date}",
  "sf.newTitle": "New Sinking Fund",
  "sf.name": "Name",
  "sf.icon": "Icon",
  "sf.namePh": "e.g. Holiday",
  "sf.fundIcon": "Fund icon",
  "sf.targetAmount": "Target",
  "sf.targetDate": "Target Date",
  "sf.alreadySaved": "Already Saved",
  "sf.color": "Color",
  "sf.empty": "No sinking funds yet. Add one to start saving!",

  "cf.title": "Monthly Cash Flow",
  "cf.titleYear": "Monthly Cash Flow · {year}",
  "cf.netSurplus": "Net Surplus",
  "cf.income": "Income",
  "cf.expenses": "Expenses",
  "cf.expense": "Expense",
  "cf.netFlow": "Net Flow",
  "cf.pctSaved": "{n}% saved",
  "cf.mode.bars": "Grouped Bars",
  "cf.mode.flow": "Smooth Flow",
  "cf.mode.net": "Net Surplus",
  "cf.avgIncome": "Avg Income",
  "cf.avgExpenses": "Avg Expenses",
  "cf.avgNet": "Avg Net Surplus",
  "cf.perMo": "/mo",
  "cf.peak": "Peak: {month} ({amount})",
  "cf.maxExp": "Max Exp: {month} ({amount})",
  "cf.png": "PNG",

  "settings.dataMgmt": "Data Management & Verification",
  "settings.backupTitle": "Backup & Restore",
  "settings.backupDesc": "Download a JSON backup of all local data, or restore from a previous export.",
  "settings.pdfTitle": "Annual PDF Report",
  "settings.pdfDesc": "Offline purpose-built PDF: cover, KPIs, cash flow, budget, 50/30/20, debts, sinking funds, and net worth — with embedded charts.",
  "settings.currencyLocale": "Currency & Locale",
  "settings.theme": "Theme",
  "settings.helpSection": "Help",
  "settings.openFaq": "Open FAQ",
  "settings.currency": "Currency",
  "settings.preview": "Preview",
  "settings.negative": "Negative:",
  "settings.compact": "Compact:",
  "settings.footer": "BudgetOS · All data is stored locally on this device.",
  "settings.deleteCategory": "Delete this category? Transactions keep their amounts but lose this category link.",
  "settings.deleteAccount": "Delete this account?",
  "settings.noCategories": "No categories yet — add one above.",
  "settings.icon": "Icon",
  "settings.name": "Name",
  "settings.monthlyTarget": "Monthly Target",
  "settings.group": "Group",
  "settings.accountNameLabel": "Account Name",
  "settings.openingBalance": "Opening Balance",
  "settings.type": "Type",
  "settings.perMonth": "{amount}/mo",
  "settings.categoryIcon": "Category icon",

  "theme.curated": "Curated looks",
  "theme.fineTune": "Fine-tune accent",
  "theme.corners": "Corners",
  "theme.accent.default": "Ocean Blue",
  "theme.accent.mint": "Pastel Mint",
  "theme.accent.sand": "Warm Sand",
  "theme.accent.midnight": "Midnight",
  "theme.accent.cyberpunk": "Cyberpunk",
  "theme.radius.none": "Sharp",
  "theme.radius.sm": "Subtle",
  "theme.radius.md": "Default",
  "theme.radius.lg": "Rounded",
  "theme.radius.full": "Pill",
  "theme.clean": "Clean",
  "theme.cleanBlurb": "Soft & friendly",
  "theme.fintech": "Fintech",
  "theme.fintechBlurb": "Dense & precise",
  "theme.minimal": "Minimal",
  "theme.minimalBlurb": "Editorial & calm",
  "theme.forest": "Forest",
  "theme.forestBlurb": "Teal & warm cream",

  "layout.brand": "BudgetOS",
  "layout.tagline": "Annual Planner",
  "layout.collapse": "Collapse",
  "app.loading": "Loading BudgetOS…",
  "app.newTx": "Add transaction",
  "app.help": "Help",

  "print.tagline": "Annual Budget & Wealth Planner · Generated {date}",
  "print.footer": "Offline-first · data stays on this device",

  "nw.month": "Month",
  "nw.totalAssets": "Total Assets ({symbol})",
  "nw.totalDebts": "Total Debts ({symbol})",

  "common.noData": "No data found",

  "currency.USD": "US Dollar ($)",
  "currency.EUR": "Euro (€)",
  "currency.GBP": "British Pound (£)",
  "currency.CAD": "Canadian Dollar (CA$)",
  "currency.AUD": "Australian Dollar (A$)",
  "currency.MXN": "Mexican Peso (MX$)",
  "currency.JPY": "Japanese Yen (¥)",
  "currency.CHF": "Swiss Franc (CHF)",
  "currency.BRL": "Brazilian Real (R$)",
  "currency.INR": "Indian Rupee (₹)",

  "palette.mint": "Mint",
  "palette.sky": "Sky",
  "palette.coral": "Coral",
  "palette.lemon": "Lemon",
  "palette.lavender": "Lavender",
};

export const uiEs: TranslationDict = {
  "type.income": "Ingreso",
  "type.expense": "Gasto",
  "type.transfer": "Transferencia",

  "person.household": "Hogar",
  "person.personal": "Personal",
  "person.partner": "Pareja",
  "person.sideHustle": "Ingresos extra",

  "method.snowball": "Bola de nieve",
  "method.avalanche": "Avalancha",

  "tx.log": "Movimientos",
  "tx.search": "Buscar…",
  "tx.allTypes": "Todos los tipos",
  "tx.allCategories": "Todas las categorías",
  "tx.fromDate": "Desde",
  "tx.toDate": "Hasta",
  "tx.minAmount": "Mín",
  "tx.maxAmount": "Máx",
  "tx.clearFilters": "Limpiar",
  "tx.entries": "{n} movimientos",
  "tx.noMatch": "Ningún movimiento coincide con los filtros.",
  "tx.loadMore": "Cargar más ({n} restantes)",
  "tx.importCsv": "Importar CSV",
  "tx.csv": "CSV",
  "tx.add": "Añadir",
  "tx.edit": "Editar",
  "tx.delete": "Eliminar",
  "tx.editAria": "Editar movimiento",
  "tx.deleteAria": "Eliminar movimiento",
  "tx.deleteConfirm": "¿Eliminar este movimiento?",
  "tx.col.date": "Fecha",
  "tx.col.type": "Tipo",
  "tx.col.amount": "Importe",
  "tx.col.category": "Categoría",
  "tx.col.account": "Cuenta",
  "tx.col.person": "Persona",
  "tx.col.notes": "Notas",

  "txModal.edit": "Editar movimiento",
  "txModal.new": "Nuevo movimiento",
  "txModal.editSub": "Actualiza un registro existente",
  "txModal.newSub": "Registra un gasto o ingreso",
  "txModal.type": "Tipo de movimiento",
  "txModal.amount": "Importe",
  "txModal.date": "Fecha",
  "txModal.person": "Persona / Miembro",
  "txModal.personPh": "Hogar, Juan, Sara…",
  "txModal.category": "Categoría",
  "txModal.chooseCategory": "— Elegir categoría —",
  "txModal.notes": "Notas / Descripción",
  "txModal.notesPh": "Detalles opcionales…",
  "txModal.recurring": "Movimiento recurrente",
  "txModal.freq.weekly": "Semanal",
  "txModal.freq.monthly": "Mensual",
  "txModal.freq.custom": "Intervalo personalizado",
  "txModal.intervalDays": "Días entre cada uno",
  "txModal.recurringHelp": "Se generan entradas futuras automáticamente. Luego puedes omitir una sola ocurrencia.",
  "txModal.series": "Serie recurrente",
  "txModal.scopeOne": "Solo esta ocurrencia",
  "txModal.scopeSeries": "Toda la serie (esta y futuras)",
  "txModal.skip": "Omitir esta ocurrencia",
  "txModal.skipConfirm": "¿Omitir solo esta ocurrencia? El resto de la serie se mantiene.",
  "txModal.saving": "Guardando…",
  "txModal.update": "Actualizar movimiento",
  "txModal.save": "Guardar movimiento",

  "csv.title": "Importar CSV",
  "csv.intro": "Asigna columnas de tu extracto bancario, previsualiza y luego importa.",
  "csv.mapping": "Asignación de columnas",
  "csv.mappingHint": "Izquierda: nombre de columna en tu CSV. Derecha: asigna cada una a un campo de BudgetOS.",
  "csv.preview": "Vista previa ({ok}/{total} válidas)",
  "csv.ignore": "Ignorar",
  "csv.ok": "OK",
  "csv.importing": "Importando…",
  "csv.importN": "Importar {n} filas",
  "csv.readError": "No se pudieron leer las cabeceras del CSV.",
  "csv.badDate": "Fecha inválida",
  "csv.badAmount": "Importe inválido",
  "csv.noCategory": "Sin categoría",

  "cal.subtitle": "Sigue gastos diarios y rachas sin gastar",
  "cal.prev": "Mes anterior",
  "cal.next": "Mes siguiente",
  "cal.streak": "Racha sin gastar",
  "cal.streakSub": "días consecutivos",
  "cal.noSpendDays": "Días sin gasto",
  "cal.thisMonth": "este mes",
  "cal.spendDays": "Días con gasto",
  "cal.active": "activos",
  "cal.totalSpent": "Total gastado",
  "cal.avgDay": "media {amount}/día",
  "cal.title": "Calendario de gastos (pulsa un día)",
  "cal.legend.noSpend": "Sin gasto",
  "cal.legend.spend": "Día con gasto",
  "cal.legend.today": "Hoy",
  "cal.badge.noSpend": "Día sin gasto",
  "cal.badge.spent": "Gastado:",
  "cal.badge.income": "Ingreso:",
  "cal.cleanTitle": "¡Racha financiera limpia!",
  "cal.cleanBody": "No hay gastos este día. ¡Sigue así!",
  "cal.txOnDate": "Movimientos de esta fecha ({n})",
  "cal.addForDate": "Añadir movimiento en esta fecha",

  "debt.title": "Calculadora de deudas",
  "debt.totalDebt": "Deuda total",
  "debt.totalInterest": "Intereses totales",
  "debt.debtFreeDate": "Fecha libre de deudas",
  "debt.apr": "{n}% TIN",
  "debt.currentBalance": "saldo actual",
  "debt.minPay": "Pago mín.",
  "debt.extra": "Extra",
  "debt.interest": "Intereses",
  "debt.months": "{n} meses",
  "debt.viewSchedule": "Ver tabla de amortización",
  "debt.hideSchedule": "Ocultar amortización",
  "debt.col.month": "Mes",
  "debt.col.payment": "Pago",
  "debt.col.principal": "Capital",
  "debt.col.interest": "Intereses",
  "debt.col.balance": "Saldo",
  "debt.balance": "Saldo ({symbol})",
  "debt.rate": "Tipo (%)",
  "debt.minPayment": "Pago mínimo",
  "debt.extraPayment": "Pago extra",
  "debt.chartTitle": "Saldo de deuda combinado en el tiempo",
  "debt.payoffOrderSnowball": "Orden de pago (menor saldo primero)",
  "debt.payoffOrderAvalanche": "Orden de pago (mayor interés primero)",
  "debt.exportSchedule": "Exportar tabla",
  "debt.edit": "Editar",

  "mb.exportMonth": "Exportar este mes",
  "mb.income": "Ingresos",
  "mb.expenses": "Gastos",
  "mb.target": "objetivo {amount}",
  "mb.budget": "presupuesto {amount}",
  "mb.allocation": "Distribución mensual del gasto",
  "mb.byGroup": "Por grupo (Necesidades / Deseos / Ahorros)",
  "mb.topExpense": "Principales categorías de gasto",
  "mb.incomeSources": "Fuentes de ingresos",

  "rule.netIncome": "Ingreso neto mensual",
  "rule.distribution": "Distribución 50/30/20",
  "rule.breakdown": "Desglose detallado",
  "rule.visual": "Comparación visual",
  "rule.targetPct": "Objetivo: {n}% del ingreso",
  "rule.actual": "Real:",
  "rule.unclassified": "{n}% del ingreso sin clasificar o sin gastar",
  "rule.status.ahead": "Por delante del objetivo",
  "rule.status.onTrack": "En camino",
  "rule.status.below": "Por debajo del objetivo",
  "rule.status.over": "Por encima del presupuesto",
  "rule.status.under": "Por debajo del presupuesto",

  "sf.title": "Fondos objetivo",
  "sf.addFund": "Añadir fondo",
  "sf.totalSaved": "Total ahorrado",
  "sf.saved": "Ahorrado",
  "sf.target": "Objetivo",
  "sf.remaining": "Restante",
  "sf.monthsLeft": "Meses restantes",
  "sf.needMonth": "Necesario/mes",
  "sf.contribute": "+ Aportar",
  "sf.contributePrompt": "Añadir a \"{name}\"\nActual: {current}\nImporte:",
  "sf.deleteConfirm": "¿Eliminar \"{name}\"?",
  "sf.due": "Vence {date}",
  "sf.newTitle": "Nuevo fondo objetivo",
  "sf.name": "Nombre",
  "sf.icon": "Icono",
  "sf.namePh": "p. ej. Vacaciones",
  "sf.fundIcon": "Icono del fondo",
  "sf.targetAmount": "Objetivo",
  "sf.targetDate": "Fecha objetivo",
  "sf.alreadySaved": "Ya ahorrado",
  "sf.color": "Color",
  "sf.empty": "Aún no hay fondos. ¡Añade uno para empezar a ahorrar!",

  "cf.title": "Flujo de caja mensual",
  "cf.titleYear": "Flujo de caja mensual · {year}",
  "cf.netSurplus": "Superávit neto",
  "cf.income": "Ingresos",
  "cf.expenses": "Gastos",
  "cf.expense": "Gasto",
  "cf.netFlow": "Flujo neto",
  "cf.pctSaved": "{n}% ahorrado",
  "cf.mode.bars": "Barras agrupadas",
  "cf.mode.flow": "Flujo suave",
  "cf.mode.net": "Superávit neto",
  "cf.avgIncome": "Ingreso medio",
  "cf.avgExpenses": "Gasto medio",
  "cf.avgNet": "Superávit medio",
  "cf.perMo": "/mes",
  "cf.peak": "Pico: {month} ({amount})",
  "cf.maxExp": "Máx. gasto: {month} ({amount})",
  "cf.png": "PNG",

  "settings.dataMgmt": "Datos y verificación",
  "settings.backupTitle": "Copia y restauración",
  "settings.backupDesc": "Descarga una copia JSON de todos los datos locales, o restaura desde una exportación anterior.",
  "settings.pdfTitle": "Informe anual PDF",
  "settings.pdfDesc": "PDF offline a medida: portada, KPIs, flujo de caja, presupuesto, 50/30/20, deudas, fondos y patrimonio — con gráficos.",
  "settings.currencyLocale": "Moneda y región",
  "settings.theme": "Tema",
  "settings.helpSection": "Ayuda",
  "settings.openFaq": "Abrir FAQ",
  "settings.currency": "Moneda",
  "settings.preview": "Vista previa",
  "settings.negative": "Negativo:",
  "settings.compact": "Compacto:",
  "settings.footer": "BudgetOS · Todos los datos se guardan en este dispositivo.",
  "settings.deleteCategory": "¿Eliminar esta categoría? Los movimientos conservan el importe pero pierden el vínculo.",
  "settings.deleteAccount": "¿Eliminar esta cuenta?",
  "settings.noCategories": "Aún no hay categorías — añade una arriba.",
  "settings.icon": "Icono",
  "settings.name": "Nombre",
  "settings.monthlyTarget": "Objetivo mensual",
  "settings.group": "Grupo",
  "settings.accountNameLabel": "Nombre de la cuenta",
  "settings.openingBalance": "Saldo inicial",
  "settings.type": "Tipo",
  "settings.perMonth": "{amount}/mes",
  "settings.categoryIcon": "Icono de categoría",

  "theme.curated": "Estilos recomendados",
  "theme.fineTune": "Ajustar acento",
  "theme.corners": "Esquinas",
  "theme.accent.default": "Azul océano",
  "theme.accent.mint": "Menta pastel",
  "theme.accent.sand": "Arena cálida",
  "theme.accent.midnight": "Medianoche",
  "theme.accent.cyberpunk": "Cyberpunk",
  "theme.radius.none": "Recto",
  "theme.radius.sm": "Sutil",
  "theme.radius.md": "Por defecto",
  "theme.radius.lg": "Redondeado",
  "theme.radius.full": "Píldora",
  "theme.clean": "Clean",
  "theme.cleanBlurb": "Suave y cercano",
  "theme.fintech": "Fintech",
  "theme.fintechBlurb": "Denso y preciso",
  "theme.minimal": "Minimal",
  "theme.minimalBlurb": "Editorial y calmado",
  "theme.forest": "Forest",
  "theme.forestBlurb": "Verde azulado y crema",

  "layout.brand": "BudgetOS",
  "layout.tagline": "Planificador anual",
  "layout.collapse": "Contraer",
  "app.loading": "Cargando BudgetOS…",
  "app.newTx": "Añadir movimiento",
  "app.help": "Ayuda",

  "print.tagline": "Presupuesto anual y patrimonio · Generado {date}",
  "print.footer": "Offline-first · los datos se quedan en este dispositivo",

  "nw.month": "Mes",
  "nw.totalAssets": "Activos totales ({symbol})",
  "nw.totalDebts": "Deudas totales ({symbol})",

  "common.noData": "Sin datos",

  "currency.USD": "Dólar estadounidense ($)",
  "currency.EUR": "Euro (€)",
  "currency.GBP": "Libra esterlina (£)",
  "currency.CAD": "Dólar canadiense (CA$)",
  "currency.AUD": "Dólar australiano (A$)",
  "currency.MXN": "Peso mexicano (MX$)",
  "currency.JPY": "Yen japonés (¥)",
  "currency.CHF": "Franco suizo (CHF)",
  "currency.BRL": "Real brasileño (R$)",
  "currency.INR": "Rupia india (₹)",

  "palette.mint": "Menta",
  "palette.sky": "Cielo",
  "palette.coral": "Coral",
  "palette.lemon": "Limón",
  "palette.lavender": "Lavanda",
};
